import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { CaretDown, Check, MagnifyingGlass, X } from '@phosphor-icons/react';
import { formatNumber, toEnDigits, toFaDigits } from '@aramis/shared';
import { cx } from './core';

const control = 'w-full rounded-control border border-control bg-surface px-3 text-sm transition-[border-color,box-shadow] duration-150 hover:border-muted focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:bg-sunken disabled:text-subtle aria-[invalid=true]:border-danger';

/** Label above, helper and error below (never placeholder-as-label). */
export function Field({ label, hint, error, children, required, className, htmlFor }: { label?: ReactNode; hint?: ReactNode; error?: string; children: ReactNode; required?: boolean; className?: string; htmlFor?: string }) {
  return (
    <div className={cx('flex min-w-0 flex-col gap-1.5', className)}>
      {label && <label htmlFor={htmlFor} className="text-[13px] font-medium text-text">{label}{required && <span className="text-danger" aria-hidden> *</span>}</label>}
      {children}
      {error ? <p role="alert" className="text-xs text-danger">{error}</p> : hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; ltr?: boolean }>(
  function TextInput({ invalid, ltr, className, ...rest }, ref) {
    return <input ref={ref} aria-invalid={invalid || undefined} dir={ltr ? 'ltr' : undefined} {...rest} className={cx(control, 'h-10', ltr && 'text-left', className)} />;
  });

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  function TextArea({ invalid, className, rows = 4, ...rest }, ref) {
    return <textarea ref={ref} rows={rows} aria-invalid={invalid || undefined} {...rest} className={cx(control, 'py-2 leading-7', className)} />;
  });

export function Select({ invalid, className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return (
    <div className="relative">
      <select aria-invalid={invalid || undefined} {...rest} className={cx(control, 'h-10 appearance-none pe-9', className)}>{children}</select>
      <CaretDown size={14} className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
    </div>
  );
}

/** Toman in words so a missing zero is obvious: 2450000 -> "۲ میلیون و ۴۵۰ هزار تومان". */
export function tomanWords(v: number | null | undefined): string {
  if (!v) return '';
  const b = Math.floor(v / 1_000_000_000), m = Math.floor((v % 1_000_000_000) / 1_000_000), t = Math.floor((v % 1_000_000) / 1000), r = v % 1000;
  const parts = [b && `${toFaDigits(b)} میلیارد`, m && `${toFaDigits(m)} میلیون`, t && `${toFaDigits(t)} هزار`, r && toFaDigits(r)].filter(Boolean);
  return `${parts.join(' و ')} تومان`;
}

export function MoneyInput({ value, onChange, invalid, placeholder, id, disabled, showWords = true }: { value: number | null | undefined; onChange: (v: number | null) => void; invalid?: boolean; placeholder?: string; id?: string; disabled?: boolean; showWords?: boolean }) {
  const [focused, setFocused] = useState(false);
  const shown = value == null ? '' : focused ? toFaDigits(value) : formatNumber(value);
  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <input id={id} inputMode="numeric" disabled={disabled} aria-invalid={invalid || undefined} placeholder={placeholder} value={shown}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          onChange={(e) => { const d = toEnDigits(e.target.value).replace(/\D/g, ''); onChange(d ? Math.min(Number(d), 10_000_000_000) : null); }}
          className={cx(control, 'num h-10 pe-14')} />
        <span className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-xs text-muted">تومان</span>
      </div>
      {showWords && value ? <span className="text-xs text-muted">{tomanWords(value)}</span> : null}
    </div>
  );
}

export function NumberInput({ value, onChange, min = 0, max, invalid, id, suffix, disabled }: { value: number | null | undefined; onChange: (v: number | null) => void; min?: number; max?: number; invalid?: boolean; id?: string; suffix?: string; disabled?: boolean }) {
  return (
    <div className="relative">
      <input id={id} inputMode="numeric" disabled={disabled} aria-invalid={invalid || undefined} value={value == null ? '' : toFaDigits(value)}
        onChange={(e) => { const d = toEnDigits(e.target.value).replace(/[^\d-]/g, ''); if (!d) return onChange(null); let n = Number(d); if (n < min) n = min; if (max != null && n > max) n = max; onChange(n); }}
        className={cx(control, 'num h-10', suffix && 'pe-12')} />
      {suffix && <span className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-xs text-muted">{suffix}</span>}
    </div>
  );
}

export function Switch({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; description?: ReactNode; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      {(label || description) && (
        <label htmlFor={id} className="min-w-0 cursor-pointer">
          {label && <div className="text-sm font-medium">{label}</div>}
          {description && <div className="text-[13px] text-muted">{description}</div>}
        </label>
      )}
      <button id={id} type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)}
        className={cx('pressable relative mt-0.5 h-6 w-11 shrink-0 rounded-control border transition-colors', checked ? 'border-accent bg-accent' : 'border-control bg-sunken', disabled && 'opacity-45')}>
        <span className={cx('absolute start-0.5 top-0.5 size-[18px] rounded-[1px] bg-surface shadow-sm transition-transform duration-(--m-dur-small) ease-(--m-ease-out)', checked && 'ltr:translate-x-5 rtl:-translate-x-5')} />
      </button>
    </div>
  );
}

export function Checkbox({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
  return (
    <label className={cx('inline-flex cursor-pointer select-none items-center gap-2 text-sm', disabled && 'cursor-not-allowed opacity-45')}>
      <button type="button" role="checkbox" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)}
        className={cx('pressable grid size-[18px] shrink-0 place-items-center rounded-control border', checked ? 'border-accent bg-accent text-on-accent' : 'border-control bg-surface')}>
        {checked && <Check size={12} weight="bold" aria-hidden />}
      </button>
      {label}
    </label>
  );
}

/** Radio cards: big, obvious choices for non-technical decisions. */
export function ChoiceGroup<T extends string>({ value, onChange, options, columns = 2 }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; description?: ReactNode }[]; columns?: number }) {
  return (
    <div role="radiogroup" className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={on} onClick={() => onChange(o.value)}
            className={cx('pressable rounded-control border p-3 text-start', on ? 'border-accent bg-accent-soft/60' : 'border-line bg-surface hover:border-control')}>
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className={cx('grid size-4 place-items-center rounded-full border', on ? 'border-accent' : 'border-control')}>{on && <span className="size-2 rounded-full bg-accent" />}</span>
              {o.label}
            </div>
            {o.description && <div className="mt-1 ps-6 text-[13px] text-muted">{o.description}</div>}
          </button>
        );
      })}
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[] }) {
  return (
    <div role="radiogroup" className="inline-flex rounded-control border border-line bg-sunken p-0.5">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} onClick={() => onChange(o.value)}
          className={cx('h-8 rounded-[1px] px-3 text-[13px] transition-colors', o.value === value ? 'bg-surface font-medium text-text shadow-sm' : 'text-muted hover:text-text')}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = 'جستجو', className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <div className={cx('relative', className)}>
      <MagnifyingGlass size={16} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder}
        className={cx(control, 'h-10 ps-9')} />
    </div>
  );
}

export function TagInput({ value, onChange, placeholder = 'بنویسید و Enter بزنید', ltr }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string; ltr?: boolean }) {
  const [draft, setDraft] = useState('');
  const add = () => { const t = draft.trim(); if (t && !value.includes(t)) onChange([...value, t]); setDraft(''); };
  return (
    <div className={cx(control, 'flex min-h-10 flex-wrap items-center gap-1.5 py-1.5')}>
      {value.map((t) => (
        <span key={t} className="inline-flex h-7 items-center gap-1 rounded-control bg-sunken ps-2 pe-1 text-[13px]">
          <span dir={ltr ? 'ltr' : undefined}>{t}</span>
          <button type="button" aria-label={`حذف ${t}`} onClick={() => onChange(value.filter((x) => x !== t))} className="grid size-5 place-items-center rounded-control text-muted hover:bg-line hover:text-text"><X size={12} /></button>
        </span>
      ))}
      <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={value.length ? '' : placeholder} dir={ltr ? 'ltr' : undefined}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',' || e.key === '،') { e.preventDefault(); add(); } else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1)); }}
        onBlur={add} className="h-7 min-w-[8ch] flex-1 bg-transparent outline-none" />
    </div>
  );
}
